import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, MessageSquare, Megaphone, Instagram, Video, Package, Lightbulb, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ai } from "@/lib/api";

const TASKS = [
  { id: "wa_campaign", icon: MessageSquare, label: "Pesan WhatsApp Campaign", desc: "Buat pesan WA untuk reminder, promo, atau winback", placeholder: "Contoh: Kirim reminder repeat order untuk pelanggan yang beli serum brightening 30 hari lalu, kasih cashback Rp15.000" },
  { id: "promo_copy", icon: Megaphone, label: "Copy Promo", desc: "Copywriting singkat untuk promo & landing page", placeholder: "Contoh: Buat copy untuk promo bundling skincare 3 in 1 diskon 30%" },
  { id: "social_caption", icon: Instagram, label: "Caption IG/TikTok", desc: "Caption engaging untuk konten sosial media", placeholder: "Contoh: Post produk baby lotion baru launch, USP-nya natural & hypoallergenic" },
  { id: "live_script", icon: Video, label: "Script Live Selling", desc: "Script 5 menit segmen live selling", placeholder: "Contoh: Live jualan kopi arabica premium, ada promo bundling + free ongkir 2 jam" },
  { id: "bundle_idea", icon: Package, label: "Ide Bundling Produk", desc: "Rekomendasi bundling untuk naikkan AOV", placeholder: "Contoh: Punya produk serum, moisturizer, dan sunscreen. Kasih 3-5 ide bundling menarik" },
  { id: "campaign_rec", icon: Lightbulb, label: "Rekomendasi Campaign", desc: "AI menganalisa & rekomendasikan campaign next step", placeholder: "Contoh: 500 pelanggan beli produk 45 hari lalu, kompetitor lagi diskon. Apa strategi campaign yang paling efektif?" },
  { id: "segmentation", icon: Users, label: "Segmentasi Pelanggan", desc: "AI buatkan segmentasi & strategi per segmen", placeholder: "Contoh: Ada 2000 customer dari Shopee, TikTok, dan IG. Bagaimana segmentasi & strategi retensi yang berbeda per segmen?" },
];

export default function AIAssistant() {
  const [task, setTask] = useState(TASKS[0]);
  const [context, setContext] = useState("");
  const [tone, setTone] = useState("friendly");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!context.trim()) { toast.error("Konteks wajib diisi"); return; }
    setLoading(true);
    setResult("");
    try {
      const res = await ai.generate({ prompt_type: task.id, context, tone });
      setResult(res.result);
      toast.success("Konten berhasil dibuat!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
    }
    setLoading(false);
  };

  const copy = () => { navigator.clipboard.writeText(result); toast.success("Disalin ke clipboard"); };

  return (
    <div className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
          <Sparkles size={12} /> Powered by Claude Sonnet 4.5
        </div>
        <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-gray-900">AI Marketing Assistant</h1>
        <p className="text-sm text-gray-500 mt-1">Generate pesan campaign, copy, caption, dan strategi dalam hitungan detik.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-1 border-gray-200">
          <CardContent className="p-3">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-400 px-2 py-2">Pilih Task</div>
            <div className="space-y-1">
              {TASKS.map((t) => {
                const Icon = t.icon;
                const active = task.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setTask(t); setResult(""); }}
                    data-testid={`ai-task-${t.id}`}
                    className={`w-full text-left flex items-start gap-2.5 p-3 rounded-lg transition-colors ${
                      active ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${active ? "text-emerald-900" : "text-gray-900"}`}>{t.label}</div>
                      <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-3 space-y-4">
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><task.icon size={18} /> {task.label}</h3>
              <p className="text-sm text-gray-500 mt-1">{task.desc}</p>

              <div className="mt-5 space-y-3">
                <div>
                  <Label>Konteks / Instruksi</Label>
                  <Textarea rows={5} value={context} onChange={(e) => setContext(e.target.value)} placeholder={task.placeholder} data-testid="ai-context" />
                </div>
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">Tone:</Label>
                  <div className="flex gap-2 flex-wrap">
                    {["friendly", "professional", "casual", "urgent"].map((t) => (
                      <button key={t} onClick={() => setTone(t)} data-testid={`tone-${t}`}
                        className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${tone === t ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"}`}>{t}</button>
                    ))}
                  </div>
                </div>
                <Button onClick={generate} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold h-11 px-6" data-testid="ai-generate-btn">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  Generate dengan AI
                </Button>
              </div>
            </CardContent>
          </Card>

          {result && (
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                    <Sparkles size={12} /> Hasil AI
                  </div>
                  <Button size="sm" variant="outline" onClick={copy} data-testid="ai-copy-btn"><Copy size={14} className="mr-1" /> Salin</Button>
                </div>
                <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">{result}</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
